import type { ScopeRef } from "@enduring-systems/entitlements";
import { Check, Copy } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { copyText } from "@/lib/clipboard";

import { scopeKindLabel } from "../scope";

export interface WorkspaceHeaderProps {
  readonly scope: ScopeRef;
  /** The workspace's name and kind, absent when the scope was entered directly. */
  readonly workspace: { readonly name: string; readonly kind: string } | null;
}

/**
 * What the operator is looking at: the workspace's name, its kind, the scope
 * kind the entitlements engine speaks, and the scope id.
 *
 * The id is shown because a support conversation needs it, and copyable because
 * an id read off a screen and retyped is an id that can land on another
 * customer. It is never an input here: a scope arrives from the search or from
 * the raw editor's own selector.
 */
export function WorkspaceHeader({ scope, workspace }: WorkspaceHeaderProps): React.ReactElement {
  /* The VALUE copied, not a boolean: derived against the scope on screen, a
     confirmation cannot outlive the id it was about and claim the operator
     holds the new workspace's id when their clipboard holds the old one. */
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const scopeId = scope.kind === "platform" ? null : scope.id;
  const copied = scopeId !== null && copiedId === scopeId;

  const copy = async (value: string): Promise<void> => {
    const ok = await copyText(value);
    setCopiedId(ok ? value : null);
  };

  return (
    <div className="space-y-2" data-testid="workspace-header">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold" data-testid="workspace-header-name">
          {workspace?.name ?? scopeKindLabel(scope.kind)}
        </h2>
        {workspace !== null && (
          <Badge variant="secondary" data-testid="workspace-header-kind">
            {workspace.kind}
          </Badge>
        )}
        <Badge variant="outline" data-testid="workspace-header-scope-kind">
          {scopeKindLabel(scope.kind)}
        </Badge>
      </div>

      {scopeId === null ? (
        <p className="text-sm text-muted-foreground" data-testid="workspace-header-no-scope-id">
          The platform scope carries no id.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <code
            className="rounded bg-muted px-1.5 py-0.5 text-xs"
            data-testid="workspace-header-scope-id"
          >
            {scopeId}
          </code>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Copy scope id"
            data-testid="workspace-header-copy"
            onClick={() => {
              void copy(scopeId);
            }}
          >
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            <span className="ml-1.5">{copied ? "Copied" : "Copy id"}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
